import React, { useCallback, useEffect, useState } from "react";
import { NumberParam, useQueryParam } from "use-query-params";
import { CashFlowTableRow } from "@common/dto/reports/cash-flow-tab-report-response.dto";
import { isFunction } from "@common/helpers/typeGuards";
import { useCashFlowPageFiltersCache } from "@hooks/view/useCashFlowPageFiltersCache";
import { useEntitiesById } from "@hooks/useEntities";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ReportFilters } from "@common/types/filters/reports";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { FilterTabs } from "../pageHeader/filters/lib";
import { CashFlowContextProvider } from "./CashFlowContextProvider";
import "./CashFlowPage.scss";
import { CashFlowPageFiltersProps } from "./CashFlowPageFilters/CashFlowPageFilters";
import { CashFlowReport } from "./CashFlowReport";
import { useDefaultReportsFilters } from "./useDefaultReportsFilters";
import { ReportBillingCta } from "./ReportBillingCta/ReportBillingCta";
import { CashFlowExportButton } from "./CashFlowExportButton";
import { CashFlowExpandCollapseButton } from "./CashFlowExpandCollapseButton";
import { CashFlowExpandProvider } from "./CashFlowExpandProvider";
import { useCashFlowContext } from "./cash-flow.context";
import { ReportsPageFilters } from "./filters/ReportsPageFilters";

const EMPTY_ROWS: CashFlowTableRow[] = [];

export const CashFlowPage: React.FC = () => {
    const defaultFilters = useDefaultReportsFilters();
    const [cachedFilters, setCachedFilters] = useCashFlowPageFiltersCache();
    const [entityIdParam] = useQueryParam("entityId", NumberParam);
    const entities = useEntitiesById();
    const [filters, setFilters] = useState({
        ...defaultFilters,
        ...cachedFilters,
    });

    const onFiltersChange = useCallback<CashFlowPageFiltersProps["onChange"]>(
        (newFilters) => {
            setFilters((oldFilters: any) => {
                const f = isFunction(newFilters)
                    ? newFilters(oldFilters)
                    : { ...oldFilters, ...newFilters };
                setCachedFilters(f);
                return f;
            });
        },
        [setCachedFilters],
    );

    useEffect(() => {
        const entity = entityIdParam && entities.get(entityIdParam);

        if (!entity) {
            return;
        }

        onFiltersChange((oldFilters) => {
            if (
                oldFilters.entityIds?.length !== 1 ||
                oldFilters.entityIds[0] !== entity.id
            ) {
                return {
                    ...oldFilters,
                    entityIds: [entity.id],
                };
            }

            return oldFilters;
        });
    }, [entityIdParam, onFiltersChange, entities]);

    return (
        <main className="reports">
            <CashFlowContextProvider filters={filters}>
                <CashFlowPageContent
                    filters={filters}
                    resetFilters={() => setFilters(defaultFilters)}
                    onFiltersChange={onFiltersChange}
                />
            </CashFlowContextProvider>
        </main>
    );
};

interface CashFlowPageContentProps {
    filters: ReportFilters;
    resetFilters: () => void;
    onFiltersChange: CashFlowPageFiltersProps["onChange"];
}

const CashFlowPageContent: React.FC<CashFlowPageContentProps> = ({
    filters,
    resetFilters,
    onFiltersChange,
}) => {
    const { cashFlowReport } = useCashFlowContext();
    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_INSIGHTS_CASH_FLOW_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <CashFlowExpandProvider
            rows={cashFlowReport?.tableData?.rows ?? EMPTY_ROWS}
        >
            <ReportBillingCta />

            <TablePageHeader
                filters={filters}
                onFiltersChange={onFiltersChange}
                availableFilters={[
                    FilterTabs.CATEGORY,
                    FilterTabs.ACCOUNT,
                    FilterTabs.CLASS,
                ]}
                isFullPage
                borderBottom
            >
                <ReportsPageFilters
                    filters={filters}
                    onChange={onFiltersChange}
                    pinnedPresets={pinnedPresets}
                    onTogglePin={onTogglePin}
                    actions={<CashFlowExpandCollapseButton />}
                    exportButton={<CashFlowExportButton filters={filters} />}
                />
            </TablePageHeader>

            <CashFlowReport
                resetFilters={resetFilters}
                onChange={onFiltersChange}
            />
        </CashFlowExpandProvider>
    );
};
